// ─────────────────────────────────────────
// Feed.js — the social feed
// ─────────────────────────────────────────
// Fetches rounds + likes + comments from Supabase.
// Likes toggle in real time (optimistic update).
// Comments expand inline below each post.

import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import EditRoundModal from '../components/EditRoundModal'
import AccountModal from '../components/AccountModal'
import AvatarImg from '../components/AvatarImg'
import Nav from '../components/Nav'
import { useIsMobile } from '../hooks/useIsMobile'

export default function Feed({ session }) {
  const isMobile = useIsMobile()
  const [rounds, setRounds] = useState([])
  const [loading, setLoading] = useState(true)

  // likes — map of roundId → { count, liked }
  // "liked" means the currently logged-in user has liked that round
  const [likes, setLikes] = useState({})

  // openComments — the round ID whose comment section is currently open (or null)
  const [openComments, setOpenComments] = useState(null)

  // comments — map of roundId → array of comment objects
  const [comments, setComments] = useState({})

  // commentText — what the user is typing in the comment box
  const [commentText, setCommentText] = useState('')
  const [commentLoading, setCommentLoading] = useState(false)

  // lightboxUrl — photo currently open full-screen
  const [lightboxUrl, setLightboxUrl] = useState(null)

  // editingRound — the round object currently open in the edit modal (or null)
  const [editingRound, setEditingRound] = useState(null)

  // account modal
  const [showAccount, setShowAccount] = useState(false)

  // share panel (desktop only — mobile uses native share sheet)
  const [showShare, setShowShare] = useState(false)
  const [shareCopied, setShareCopied] = useState(false)

  // compose box — quick post from the feed
  const [myProfile, setMyProfile] = useState(null)
  const [postText, setPostText] = useState('')
  const [postMedia, setPostMedia] = useState(null)
  const [postMediaPreview, setPostMediaPreview] = useState(null)
  const [postMediaIsVideo, setPostMediaIsVideo] = useState(false)
  const [posting, setPosting] = useState(false)

  useEffect(() => {
    fetchFeed()
    fetchMyProfile()

    // Real-time: new posts appear instantly without refreshing
    const channel = supabase
      .channel('feed')
      .on('postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'rounds' },
        payload => setRounds(prev => [payload.new, ...prev])
      )
      .subscribe()

    return () => supabase.removeChannel(channel)
  }, [])

  // ── Share / Invite ─────────────────────────────────────────
  function getInviteUrl() {
    return `https://breakie.golf/invite/${myProfile?.username || ''}`
  }

  async function handleShare() {
    const url = getInviteUrl()
    const text = `Join me on Breakie — the golf social app! 🏌️⛳`
    if (navigator.share) {
      try {
        await navigator.share({ title: 'Join me on Breakie', text, url })
      } catch (e) {
        // user cancelled — no action needed
      }
    } else {
      setShowShare(prev => !prev)
    }
  }

  async function copyInviteLink() {
    try {
      await navigator.clipboard.writeText(getInviteUrl())
      setShareCopied(true)
      setTimeout(() => setShareCopied(false), 2000)
    } catch (e) {
      // fallback for older browsers
      const el = document.createElement('textarea')
      el.value = getInviteUrl()
      document.body.appendChild(el)
      el.select()
      document.execCommand('copy')
      document.body.removeChild(el)
      setShareCopied(true)
      setTimeout(() => setShareCopied(false), 2000)
    }
  }

  async function fetchMyProfile() {
    const { data } = await supabase
      .from('profiles')
      .select('username, avatar_emoji, avatar_url')
      .eq('id', session.user.id)
      .single()
    if (data) setMyProfile(data)
  }

  async function fetchFeed() {
    // Get the list of people I'm following
    const { data: followRows } = await supabase
      .from('follows')
      .select('following_id')
      .eq('follower_id', session.user.id)

    const followingIds = (followRows || []).map(r => r.following_id)
    // Always include own posts too
    const visibleIds = [...new Set([session.user.id, ...followingIds])]

    const { data, error } = await supabase
      .from('rounds')
      .select(`*, profiles ( username, handicap, avatar_emoji, avatar_url )`)
      .in('user_id', visibleIds)
      .order('created_at', { ascending: false })
      .limit(50)

    if (error || !data) { setLoading(false); return }

    setRounds(data)
    setLoading(false)

    // Now fetch all likes for these rounds in one query
    // This tells us: how many likes each round has, and whether YOU liked it
    const roundIds = data.map(r => r.id)
    if (roundIds.length === 0) return

    const { data: likesData } = await supabase
      .from('likes')
      .select('round_id, user_id')
      .in('round_id', roundIds)

    // Build the likes map from the raw rows
    const likesMap = {}
    roundIds.forEach(id => { likesMap[id] = { count: 0, liked: false } })
    ;(likesData || []).forEach(row => {
      likesMap[row.round_id].count++
      if (row.user_id === session.user.id) {
        likesMap[row.round_id].liked = true
      }
    })
    setLikes(likesMap)
  }

  // ── LIKE TOGGLE ─────────────────────────────────────────────
  // Optimistic update: we change the count in state immediately
  // so it feels instant, then write to the database in the background.
  async function handleLike(roundId) {
    const current = likes[roundId] || { count: 0, liked: false }

    if (current.liked) {
      // Already liked — remove the like
      setLikes(prev => ({
        ...prev,
        [roundId]: { count: prev[roundId].count - 1, liked: false }
      }))
      await supabase
        .from('likes')
        .delete()
        .eq('round_id', roundId)
        .eq('user_id', session.user.id)
    } else {
      // Not liked yet — add a like
      setLikes(prev => ({
        ...prev,
        [roundId]: { count: (prev[roundId]?.count || 0) + 1, liked: true }
      }))
      await supabase
        .from('likes')
        .insert({ round_id: roundId, user_id: session.user.id })
    }
  }

  // ── COMMENTS ────────────────────────────────────────────────
  // Toggle the comment section open/closed for a given round.
  // First time opening: fetch comments from the database.
  async function toggleComments(roundId) {
    if (openComments === roundId) {
      // Already open — close it
      setOpenComments(null)
      return
    }

    setOpenComments(roundId)

    // Only fetch if we haven't loaded comments for this round yet
    if (comments[roundId]) return

    const { data } = await supabase
      .from('comments')
      .select(`*, profiles ( username, avatar_emoji, avatar_url )`)
      .eq('round_id', roundId)
      .order('created_at', { ascending: true })

    setComments(prev => ({ ...prev, [roundId]: data || [] }))
  }

  // Post a new comment on a round
  async function handleComment(roundId) {
    if (!commentText.trim()) return
    setCommentLoading(true)

    const { data, error } = await supabase
      .from('comments')
      .insert({
        round_id: roundId,
        user_id: session.user.id,
        content: commentText.trim()
      })
      .select(`*, profiles ( username, avatar_emoji, avatar_url )`)
      .single()

    if (!error && data) {
      // Add the new comment to the local list immediately
      setComments(prev => ({
        ...prev,
        [roundId]: [...(prev[roundId] || []), data]
      }))
      setCommentText('')
    }

    setCommentLoading(false)
  }

  // ── EDIT / DELETE ────────────────────────────────────────────
  // Called by EditRoundModal after a successful save.
  // Updates the round in-place in state so the feed re-renders instantly.
  function handleRoundSaved(updatedRound) {
    setRounds(prev => prev.map(r => r.id === updatedRound.id ? { ...r, ...updatedRound } : r))
    setEditingRound(null)
  }

  // Called by EditRoundModal after a successful delete.
  // Removes the round from state so it disappears immediately.
  function handleRoundDeleted(roundId) {
    setRounds(prev => prev.filter(r => r.id !== roundId))
    setEditingRound(null)
  }

  // ── QUICK POST ───────────────────────────────────────────────
  function handlePostMediaChange(e) {
    const file = e.target.files[0]
    if (!file) return
    const isVideo = file.type.startsWith('video/')
    setPostMedia(file)
    setPostMediaPreview(URL.createObjectURL(file))
    setPostMediaIsVideo(isVideo)
  }

  function clearPostMedia() {
    setPostMedia(null)
    setPostMediaPreview(null)
    setPostMediaIsVideo(false)
  }

  async function handlePost() {
    if (!postText.trim() && !postMedia) return
    setPosting(true)

    let photoUrl = null
    let videoUrl = null
    if (postMedia) {
      const ext = postMedia.name.split('.').pop()
      const filePath = `${session.user.id}/${Date.now()}.${ext}`
      const { error: uploadError } = await supabase.storage
        .from('round-photos')
        .upload(filePath, postMedia)
      if (!uploadError) {
        const { data: { publicUrl } } = supabase.storage
          .from('round-photos')
          .getPublicUrl(filePath)
        if (postMediaIsVideo) videoUrl = publicUrl
        else photoUrl = publicUrl
      }
    }

    await supabase.from('rounds').insert({
      user_id: session.user.id,
      course_name: '',
      score: null,
      data_source: 'Post',
      caption: postText.trim() || null,
      photo_url: photoUrl,
      video_url: videoUrl
    })

    setPostText('')
    clearPostMedia()
    setPosting(false)
  }

  // ── HELPERS ─────────────────────────────────────────────────

  function scoreDiff(score, par) {
    if (!par) return null
    return score - par
  }

  function scoreBadgeColor(diff) {
    if (diff === null) return '#1a7a3c'
    if (diff < 0) return '#1a7a3c'
    if (diff === 0) return '#2980b9'
    return '#c0392b'
  }

  function scoreDiffLabel(diff) {
    if (diff === null) return ''
    if (diff === 0) return 'E'
    if (diff > 0) return `+${diff}`
    return `${diff}`
  }

  function timeAgo(dateStr) {
    const diff = Math.floor((Date.now() - new Date(dateStr)) / 1000)
    if (diff < 60) return 'just now'
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  }

  // ── RENDER ──────────────────────────────────────────────────

  return (
    <div style={{ ...styles.page, paddingBottom: isMobile ? 72 : 0 }}>

      {/* ── LIGHTBOX ── */}
      {lightboxUrl && (
        <div style={styles.lightboxOverlay} onClick={() => setLightboxUrl(null)}>
          <img src={lightboxUrl} alt="Round photo" style={styles.lightboxImg} />
          <button style={styles.lightboxClose}>✕</button>
        </div>
      )}

      {/* ── EDIT ROUND MODAL ── */}
      {editingRound && (
        <EditRoundModal
          round={editingRound}
          onSave={handleRoundSaved}
          onDelete={handleRoundDeleted}
          onClose={() => setEditingRound(null)}
        />
      )}

      {/* ── ACCOUNT MODAL ── */}
      {showAccount && (
        <AccountModal
          session={session}
          onClose={() => setShowAccount(false)}
          onProfileUpdated={updated => setMyProfile(prev => ({ ...prev, ...updated }))}
        />
      )}

      {/* Top bar */}
      <div style={styles.topbar}>
        <div style={styles.logo}>BREAKIE</div>
        <div style={styles.topbarRight}>
          <Link to="/log" style={styles.postBtn}>+ Log Round</Link>
          <div style={{ position: 'relative' }}>
            <button style={styles.shareBtn} onClick={handleShare} title="Invite Friends">
              📤
            </button>

            {/* ── Desktop share panel ── */}
            {showShare && (
              <>
                <div style={styles.shareBacking} onClick={() => setShowShare(false)} />
                <div style={styles.sharePanel}>
                  <div style={styles.sharePanelTitle}>Invite Friends</div>
                  <div style={styles.shareUrl}>{getInviteUrl()}</div>

                  {/* Copy link */}
                  <button style={styles.shareRow} onClick={copyInviteLink}>
                    <span style={styles.shareIcon}>🔗</span>
                    <span style={styles.shareLabel}>{shareCopied ? '✓ Copied!' : 'Copy Link'}</span>
                  </button>

                  {/* WhatsApp */}
                  <a
                    style={styles.shareRow}
                    href={`https://wa.me/?text=${encodeURIComponent(`Join me on Breakie 🏌️⛳ ${getInviteUrl()}`)}`}
                    target="_blank" rel="noreferrer"
                    onClick={() => setShowShare(false)}
                  >
                    <span style={styles.shareIcon}>💬</span>
                    <span style={styles.shareLabel}>WhatsApp</span>
                  </a>

                  {/* Twitter/X */}
                  <a
                    style={styles.shareRow}
                    href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(`Join me on Breakie — the golf social app 🏌️⛳`)}&url=${encodeURIComponent(getInviteUrl())}`}
                    target="_blank" rel="noreferrer"
                    onClick={() => setShowShare(false)}
                  >
                    <span style={styles.shareIcon}>🐦</span>
                    <span style={styles.shareLabel}>Twitter / X</span>
                  </a>

                  {/* Facebook */}
                  <a
                    style={styles.shareRow}
                    href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(getInviteUrl())}`}
                    target="_blank" rel="noreferrer"
                    onClick={() => setShowShare(false)}
                  >
                    <span style={styles.shareIcon}>📘</span>
                    <span style={styles.shareLabel}>Facebook</span>
                  </a>

                  {/* Instagram — no direct share URL, copy link instead */}
                  <button
                    style={styles.shareRow}
                    onClick={() => { copyInviteLink(); setShowShare(false) }}
                  >
                    <span style={styles.shareIcon}>📸</span>
                    <span style={styles.shareLabel}>Instagram (copy link)</span>
                  </button>

                  {/* Email */}
                  <a
                    style={styles.shareRow}
                    href={`mailto:?subject=${encodeURIComponent('Join me on Breakie!')}&body=${encodeURIComponent(`Hey! Join me on Breakie — the golf social app.\n\n${getInviteUrl()}`)}`}
                    onClick={() => setShowShare(false)}
                  >
                    <span style={styles.shareIcon}>✉️</span>
                    <span style={styles.shareLabel}>Email</span>
                  </a>
                </div>
              </>
            )}
          </div>

          <button
            style={styles.avatarBtn}
            onClick={() => setShowAccount(true)}
            title="My Account"
          >
            <AvatarImg profile={myProfile} />
          </button>
        </div>
      </div>

      <Nav />

      {/* Feed */}
      <div style={styles.feed}>

        {/* ── COMPOSE BOX ── */}
        <div style={styles.composeBox}>
          <div style={styles.composeTop}>
            <div style={styles.avatar}><AvatarImg profile={myProfile} /></div>
            <textarea
              style={styles.composeTextarea}
              placeholder="Share something with your crew..."
              value={postText}
              onChange={e => setPostText(e.target.value)}
              rows={postText ? 3 : 2}
            />
          </div>

          {postMediaPreview && (
            <div style={styles.composePhotoWrapper}>
              {postMediaIsVideo ? (
                <video src={postMediaPreview} style={styles.composePhotoImg} controls playsInline />
              ) : (
                <img src={postMediaPreview} alt="Post preview" style={styles.composePhotoImg} />
              )}
              <button style={styles.composePhotoRemove} onClick={clearPostMedia}>✕</button>
            </div>
          )}

          <div style={styles.composeFooter}>
            <div style={{ display: 'flex', gap: 6 }}>
              <label style={styles.composePhotoBtn}>
                📷
                <input type="file" accept="image/*" style={{ display: 'none' }} onChange={handlePostMediaChange} />
              </label>
              <label style={styles.composePhotoBtn}>
                🎥
                <input type="file" accept="video/*" style={{ display: 'none' }} onChange={handlePostMediaChange} />
              </label>
            </div>
            <button
              style={{
                ...styles.composePostBtn,
                opacity: (postText.trim() || postMedia) && !posting ? 1 : 0.4
              }}
              onClick={handlePost}
              disabled={(!postText.trim() && !postMedia) || posting}
            >
              {posting ? 'Posting...' : 'Post'}
            </button>
          </div>
        </div>

        {loading && <div style={styles.empty}>Loading rounds... ⛳</div>}

        {!loading && rounds.length === 0 && (
          <div style={styles.emptyCard}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>🏌️</div>
            <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 8 }}>Nothing in your feed yet</div>
            <div style={{ color: '#6b7c6e', fontSize: 14, marginBottom: 20, lineHeight: 1.5 }}>
              Follow other golfers to see their rounds here, or log your own.
            </div>
            <Link to="/leaderboard" style={{ ...styles.postBtn, marginBottom: 10, display: 'inline-block' }}>Find Golfers to Follow →</Link>
            <br />
            <Link to="/log" style={{ ...styles.postBtn, background: 'transparent', color: '#1a7a3c', border: '1.5px solid #1a7a3c' }}>Log a Round</Link>
          </div>
        )}

        {rounds.map(round => {
          const diff = scoreDiff(round.score, round.par)
          const roundLikes = likes[round.id] || { count: 0, liked: false }
          const roundComments = comments[round.id] || []
          const isCommentsOpen = openComments === round.id

          return (
            <div key={round.id} style={styles.post}>

              {/* ── Header ── */}
              <div style={styles.postHeader}>
                <Link to={round.user_id === session.user.id ? '/passport' : `/passport/${round.user_id}`} style={styles.avatarLink}>
                  <div style={styles.avatar}>
                    <AvatarImg profile={round.profiles} />
                  </div>
                </Link>
                <div style={styles.postMeta}>
                  <Link to={round.user_id === session.user.id ? '/passport' : `/passport/${round.user_id}`} style={styles.postNameLink}>
                    @{round.profiles?.username || 'golfer'}
                  </Link>
                  <div style={styles.postDetail}>
                    {timeAgo(round.created_at)}
                    {round.data_source && ` · via ${round.data_source}`}
                  </div>
                </div>
                {/* Score badge — only shown for actual rounds, not quick posts */}
                {round.data_source !== 'Post' && (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
                    <div
                      style={{
                        ...styles.scoreBadge,
                        background: scoreBadgeColor(diff),
                        cursor: round.user_id === session.user.id ? 'pointer' : 'default',
                        opacity: 1
                      }}
                      onClick={() => round.user_id === session.user.id && setEditingRound(round)}
                      title={round.user_id === session.user.id ? 'Tap to edit or delete' : ''}
                    >
                      <div style={styles.scoreNum}>{round.score}</div>
                      {diff !== null && (
                        <div style={styles.scoreLabel}>{scoreDiffLabel(diff)}</div>
                      )}
                      {round.user_id === session.user.id && (
                        <div style={styles.editHint}>✎</div>
                      )}
                    </div>
                    {round.net_score != null && (
                      <div style={styles.netBadge}>NET {round.net_score}</div>
                    )}
                  </div>
                )}
              </div>

              {/* ── Course chip — only for actual rounds, not quick posts ── */}
              {round.data_source !== 'Post' && round.course_name && (
                <Link
                  to={`/course?name=${encodeURIComponent(round.course_name)}`}
                  style={{ textDecoration: 'none' }}
                >
                  <div style={styles.courseChip}>
                    <div>
                      <div style={styles.courseName}>{round.course_name}</div>
                      {round.par && <div style={styles.courseMeta}>Par {round.par}</div>}
                    </div>
                    <div style={{ fontSize: 22 }}>⛳ →</div>
                  </div>
                </Link>
              )}

              {/* ── Stats row ── */}
              {(round.fairways || round.gir || round.putts) && (
                <div style={styles.statsRow}>
                  {round.fairways != null && (
                    <div style={styles.stat}>
                      <div style={styles.statVal}>{round.fairways}</div>
                      <div style={styles.statLabel}>FIR</div>
                    </div>
                  )}
                  {round.gir != null && (
                    <div style={styles.stat}>
                      <div style={styles.statVal}>{round.gir}</div>
                      <div style={styles.statLabel}>GIR</div>
                    </div>
                  )}
                  {round.putts != null && (
                    <div style={styles.stat}>
                      <div style={styles.statVal}>{round.putts}</div>
                      <div style={styles.statLabel}>Putts</div>
                    </div>
                  )}
                </div>
              )}

              {/* ── Photo ── */}
              {round.photo_url && (
                <div
                  style={styles.roundPhotoWrapper}
                  onClick={() => setLightboxUrl(round.photo_url)}
                >
                  <img
                    src={round.photo_url}
                    alt={`${round.course_name} round`}
                    style={styles.roundPhoto}
                  />
                  <div style={styles.photoTapHint}>tap to expand</div>
                </div>
              )}

              {/* ── Video ── */}
              {round.video_url && (
                <div style={styles.roundVideoWrapper}>
                  <video
                    src={round.video_url}
                    style={styles.roundVideo}
                    controls
                    playsInline
                    preload="metadata"
                  />
                </div>
              )}

              {/* ── Caption ── */}
              {round.caption && (
                <div style={styles.caption}>{round.caption}</div>
              )}

              {/* ── Action bar ── */}
              <div style={styles.actions}>

                {/* 👏 Clap / like button — fills with color when liked */}
                <button
                  style={{
                    ...styles.actionBtn,
                    color: roundLikes.liked ? '#1a7a3c' : '#6b7c6e',
                    fontWeight: roundLikes.liked ? 700 : 600
                  }}
                  onClick={() => handleLike(round.id)}
                >
                  👏 {roundLikes.count > 0 ? roundLikes.count : ''} Clap{roundLikes.count !== 1 ? 's' : ''}
                </button>

                {/* 💬 Comment button — shows count, toggles comment section */}
                <button
                  style={{
                    ...styles.actionBtn,
                    color: isCommentsOpen ? '#1a7a3c' : '#6b7c6e',
                    fontWeight: isCommentsOpen ? 700 : 600
                  }}
                  onClick={() => toggleComments(round.id)}
                >
                  💬 {roundComments.length > 0 ? `${roundComments.length} ` : ''}Comment{roundComments.length !== 1 ? 's' : ''}
                </button>

              </div>

              {/* ── Comment section ── */}
              {/* Slides open when the comment button is tapped */}
              {isCommentsOpen && (
                <div style={styles.commentSection}>

                  {/* Existing comments */}
                  {roundComments.length === 0 && (
                    <div style={styles.noComments}>No comments yet — be the first to talk trash 🏌️</div>
                  )}
                  {roundComments.map(c => (
                    <div key={c.id} style={styles.commentRow}>
                      <div style={styles.commentAvatar}>
                        <AvatarImg profile={c.profiles} />
                      </div>
                      <div style={styles.commentBody}>
                        <span style={styles.commentUsername}>@{c.profiles?.username || 'golfer'}</span>
                        {' '}
                        <span style={styles.commentText}>{c.content}</span>
                        <div style={styles.commentTime}>{timeAgo(c.created_at)}</div>
                      </div>
                    </div>
                  ))}

                  {/* New comment input */}
                  <div style={styles.commentInputRow}>
                    <input
                      style={styles.commentInput}
                      placeholder="Add a comment..."
                      value={commentText}
                      onChange={e => setCommentText(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && handleComment(round.id)}
                      maxLength={280}
                    />
                    <button
                      style={{
                        ...styles.commentSubmit,
                        opacity: commentText.trim() ? 1 : 0.4
                      }}
                      onClick={() => handleComment(round.id)}
                      disabled={commentLoading || !commentText.trim()}
                    >
                      Post
                    </button>
                  </div>

                </div>
              )}

            </div>
          )
        })}
      </div>
    </div>
  )
}

// ── STYLES ────────────────────────────────────────────────────

const styles = {
  page: { minHeight: '100vh', background: '#f4f7f5', fontFamily: "'Barlow', sans-serif" },

  // ── Lightbox ──────────────────────────────────────────
  lightboxOverlay: {
    position: 'fixed', inset: 0,
    background: 'rgba(0,0,0,0.92)',
    zIndex: 1000,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: 16
  },
  lightboxImg: { maxWidth: '100%', maxHeight: '90vh', objectFit: 'contain', borderRadius: 8 },
  lightboxClose: {
    position: 'absolute', top: 16, right: 16,
    background: 'rgba(255,255,255,0.15)', border: 'none', color: 'white',
    borderRadius: '50%', width: 36, height: 36, fontSize: 16, cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center'
  },

  // ── Top bar ───────────────────────────────────────────
  topbar: {
    background: '#0e1f13', padding: '14px 20px',
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    position: 'sticky', top: 0, zIndex: 10
  },
  logo: {
    fontFamily: "'Barlow Condensed', sans-serif",
    fontWeight: 900, fontSize: 26, color: '#c8f135', letterSpacing: 2
  },
  topbarRight: { display: 'flex', gap: 10, alignItems: 'center' },
  postBtn: {
    background: '#c8f135', color: '#0e1f13', border: 'none', borderRadius: 10,
    padding: '8px 16px', fontWeight: 800, fontSize: 13, cursor: 'pointer',
    textDecoration: 'none', fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: 1
  },
  shareBtn: {
    width: 36, height: 36, borderRadius: '50%',
    background: 'rgba(255,255,255,0.1)',
    border: '1.5px solid rgba(255,255,255,0.15)',
    fontSize: 16, cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    flexShrink: 0
  },
  shareBacking: {
    position: 'fixed', inset: 0, zIndex: 299
  },
  sharePanel: {
    position: 'absolute', top: 44, right: 0, zIndex: 300,
    background: 'white', borderRadius: 16,
    boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
    minWidth: 240, overflow: 'hidden'
  },
  sharePanelTitle: {
    fontFamily: "'Barlow Condensed', sans-serif",
    fontWeight: 800, fontSize: 13, color: '#0e1f13',
    letterSpacing: 1, textTransform: 'uppercase',
    padding: '12px 16px 8px', borderBottom: '1px solid #f0f4f1'
  },
  shareUrl: {
    fontSize: 10, color: '#9aaa9d', padding: '6px 16px 10px',
    borderBottom: '1px solid #f0f4f1',
    wordBreak: 'break-all', lineHeight: 1.4
  },
  shareRow: {
    display: 'flex', alignItems: 'center', gap: 12,
    padding: '11px 16px', width: '100%', textAlign: 'left',
    background: 'none', border: 'none', borderBottom: '1px solid #f8faf8',
    cursor: 'pointer', textDecoration: 'none', color: '#0e1f13',
    fontFamily: "'Barlow', sans-serif", fontSize: 14, fontWeight: 600
  },
  shareIcon: { fontSize: 18, flexShrink: 0, width: 24, textAlign: 'center' },
  shareLabel: { flex: 1 },

  avatarBtn: {
    width: 38, height: 38, borderRadius: '50%',
    background: 'rgba(255,255,255,0.12)',
    border: '2px solid rgba(200,241,53,0.4)',
    fontSize: 20, cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    flexShrink: 0
  },


  // ── Feed ──────────────────────────────────────────────
  feed: { maxWidth: 600, margin: '0 auto', padding: '16px 16px' },
  empty: { textAlign: 'center', padding: 48, color: '#6b7c6e' },
  emptyCard: {
    background: 'white', borderRadius: 18, padding: 36, textAlign: 'center',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
  },

  // ── Compose box ───────────────────────────────────────
  composeBox: {
    background: 'white', borderRadius: 18, marginBottom: 14,
    overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    padding: '14px 16px'
  },
  composeTop: { display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: 10 },
  composeTextarea: {
    flex: 1, border: '1.5px solid #e2ebe4', borderRadius: 12,
    padding: '10px 12px', fontSize: 14, color: '#0e1f13',
    fontFamily: "'Barlow', sans-serif", resize: 'none', outline: 'none',
    lineHeight: 1.4, background: '#f9fbf9'
  },
  composePhotoWrapper: {
    position: 'relative', borderRadius: 12, overflow: 'hidden',
    marginBottom: 10, border: '1.5px solid #e2ebe4', maxHeight: 220
  },
  composePhotoImg: { width: '100%', maxHeight: 220, objectFit: 'cover', display: 'block' },
  composePhotoRemove: {
    position: 'absolute', top: 8, right: 8,
    background: 'rgba(0,0,0,0.55)', color: 'white', border: 'none',
    borderRadius: '50%', width: 26, height: 26, fontSize: 11,
    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center'
  },
  composeFooter: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    borderTop: '1px solid #f0f4f1', paddingTop: 10
  },
  composePhotoBtn: {
    display: 'flex', alignItems: 'center', gap: 5,
    fontSize: 13, fontWeight: 600, color: '#6b7c6e', cursor: 'pointer',
    background: '#f4f7f5', borderRadius: 8, padding: '6px 12px',
    fontFamily: "'Barlow', sans-serif"
  },
  composePostBtn: {
    background: '#0e1f13', color: '#c8f135', border: 'none',
    borderRadius: 10, padding: '8px 20px', fontSize: 13,
    fontWeight: 800, cursor: 'pointer',
    fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: 0.5
  },

  // ── Post card ─────────────────────────────────────────
  post: {
    background: 'white', borderRadius: 18, marginBottom: 14,
    overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
  },
  postHeader: { display: 'flex', alignItems: 'center', padding: '14px 16px 10px', gap: 10 },
  avatarLink: { textDecoration: 'none', flexShrink: 0 },
  avatar: {
    width: 44, height: 44, borderRadius: '50%',
    background: 'linear-gradient(135deg,#2aad56,#1a7a3c)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 22, flexShrink: 0
  },
  postMeta: { flex: 1 },
  postNameLink: { fontWeight: 700, fontSize: 14, color: '#0e1f13', textDecoration: 'none' },
  postName: { fontWeight: 700, fontSize: 14, color: '#0e1f13' },
  postDetail: { fontSize: 11, color: '#6b7c6e', marginTop: 2 },
  scoreBadge: { borderRadius: 10, padding: '4px 14px', textAlign: 'center', minWidth: 56 },
  scoreNum: {
    fontFamily: "'Barlow Condensed', sans-serif",
    fontWeight: 900, fontSize: 28, color: 'white', lineHeight: 1.1
  },
  scoreLabel: { fontSize: 10, color: 'rgba(255,255,255,0.8)', fontWeight: 600, letterSpacing: 0.5 },
  editHint: { fontSize: 9, color: 'rgba(255,255,255,0.6)', letterSpacing: 0.3, marginTop: 1 },
  netBadge: {
    background: 'rgba(26,122,60,0.12)', border: '1px solid rgba(26,122,60,0.25)',
    borderRadius: 8, padding: '3px 8px', fontSize: 10, fontWeight: 700,
    color: '#1a7a3c', letterSpacing: 0.5, textAlign: 'center'
  },
  courseChip: {
    margin: '0 16px 12px', background: '#e8f5ed', borderRadius: 12,
    padding: '12px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center'
  },
  courseName: { fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 800, fontSize: 16, color: '#0e1f13' },
  courseMeta: { fontSize: 11, color: '#6b7c6e', marginTop: 2 },
  statsRow: { display: 'flex', margin: '0 16px 12px', gap: 8 },
  stat: { flex: 1, background: '#f4f7f5', borderRadius: 10, padding: '8px 4px', textAlign: 'center' },
  statVal: { fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 800, fontSize: 18, color: '#0e1f13', lineHeight: 1 },
  statLabel: { fontSize: 9, color: '#6b7c6e', textTransform: 'uppercase', letterSpacing: 0.5, marginTop: 2 },

  // ── Photo ─────────────────────────────────────────────
  roundPhotoWrapper: {
    position: 'relative', width: '100%', paddingBottom: '125%',
    overflow: 'hidden', cursor: 'pointer', margin: '0 0 4px'
  },
  roundPhoto: {
    position: 'absolute', top: 0, left: 0,
    width: '100%', height: '100%', objectFit: 'cover', display: 'block'
  },
  photoTapHint: {
    position: 'absolute', bottom: 8, right: 10, fontSize: 10,
    color: 'rgba(255,255,255,0.7)', fontWeight: 600, letterSpacing: 0.3,
    textShadow: '0 1px 3px rgba(0,0,0,0.5)'
  },

  roundVideoWrapper: {
    width: '100%', margin: '0 0 4px', background: '#000'
  },
  roundVideo: {
    width: '100%', maxHeight: 400, display: 'block', objectFit: 'contain'
  },

  caption: { padding: '0 16px 10px', fontSize: 13, color: '#0e1f13', lineHeight: 1.4 },

  // ── Action bar ────────────────────────────────────────
  actions: {
    display: 'flex', padding: '10px 16px 14px',
    gap: 16, borderTop: '1px solid #e2ebe4'
  },
  actionBtn: {
    background: 'none', border: 'none', fontSize: 13,
    cursor: 'pointer', padding: 0, fontFamily: "'Barlow', sans-serif"
  },

  // ── Comment section ───────────────────────────────────
  // Opens inline below the action bar
  commentSection: {
    borderTop: '1px solid #e2ebe4',
    background: '#fafcfa',
    padding: '12px 16px 14px'
  },
  noComments: {
    fontSize: 12, color: '#6b7c6e', textAlign: 'center',
    padding: '8px 0 12px', fontStyle: 'italic'
  },
  commentRow: {
    display: 'flex', gap: 8, marginBottom: 10, alignItems: 'flex-start'
  },
  commentAvatar: {
    width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
    background: 'linear-gradient(135deg,#2aad56,#1a7a3c)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14
  },
  commentBody: { flex: 1 },
  commentUsername: { fontWeight: 700, fontSize: 12, color: '#0e1f13' },
  commentText: { fontSize: 13, color: '#2d3e30', lineHeight: 1.4 },
  commentTime: { fontSize: 10, color: '#6b7c6e', marginTop: 2 },

  // The input row at the bottom of the comment section
  commentInputRow: { display: 'flex', gap: 8, marginTop: 10 },
  commentInput: {
    flex: 1, background: 'white', border: '1.5px solid #e2ebe4',
    borderRadius: 20, padding: '8px 14px', fontSize: 13,
    color: '#0e1f13', outline: 'none', fontFamily: "'Barlow', sans-serif"
  },
  commentSubmit: {
    background: '#0e1f13', color: '#c8f135', border: 'none',
    borderRadius: 20, padding: '8px 16px', fontSize: 12,
    fontWeight: 800, cursor: 'pointer',
    fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: 0.5
  }
}
